import { cn } from "./cn";

/*
 * Mirrors the shared ContactItem in qr-web-frontend (resolved to the scanned
 * page's real mobile width) and the existing Vcard contact rows in this repo:
 * a 40px circular icon chip with p-[11px], an event-500 label, a font-medium
 * value, and a my-[15px] divider rendered after every row. `href` makes the
 * row a `tel:` / `mailto:` / website link.
 */
const ContactRow = ({
  icon,
  label,
  value,
  valueColor,
  href,
  textFont,
  isName = false,
  showDivider = true,
  dividerClassName = "my-[15px]",
}) => {
  const inner = (
    <>
      <div className="bg-event-55 flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-[11px]">
        {icon}
      </div>
      <div
        className={cn(
          "flex w-full min-w-0 flex-col justify-center text-left rtl:text-right",
          !isName && "truncate",
        )}
      >
        <span
          className={cn(
            "text-event-500 rtl:mr-2 rtl:text-right",
            isName ? "text-xs" : "text-[10px] xxs:text-xs",
          )}
          style={textFont ? { fontFamily: textFont } : undefined}
        >
          {label}
        </span>
        <span
          className={cn(
            "font-medium rtl:mr-2 rtl:text-right",
            isName
              ? "word-break-fix w-full text-sm xs:text-base"
              : "truncate text-xs xxs:text-sm xs:text-base",
          )}
          style={{ color: valueColor ?? "#000000", fontFamily: textFont }}
        >
          {value}
        </span>
      </div>
    </>
  );

  /*
   * Only website (http/https) rows open in a new tab; `tel:`/`mailto:` must
   * stay same-tab so the dialer / mail client opens reliably on mobile instead
   * of leaving an empty blank tab behind. Rows without an action (the contact
   * name) render as a non-interactive div rather than an inert button.
   */
  const isExternal = href ? /^https?:\/\//i.test(href) : false;
  const className = cn(
    "flex w-full flex-row rtl:space-x-reverse",
    isName ? "items-center space-x-[15px]" : "space-x-[10px] xs:space-x-[16px]",
  );

  let control;
  if (href) {
    control = (
      <a
        href={href}
        className={cn(className, "cursor-pointer hover:opacity-80")}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {inner}
      </a>
    );
  } else {
    control = <div className={className}>{inner}</div>;
  }

  return (
    <div className="w-full">
      {control}
      {showDivider && (
        <div
          className={cn(
            "bg-event-85 h-px w-full rounded-full",
            dividerClassName,
          )}
        />
      )}
    </div>
  );
};

export default ContactRow;
